@if (columns().length > 0) {
  <div class="space-y-0">
    @for (group of visibleGroups(); track group.status; let first = $first) {
      <section
        class="gl-group-shell pt-6 mt-6 px-4 py-4"
        [class.mt-0]="first"
        [class.pt-0]="first"
        [class.gl-group-shell--stage]="!!group.color"
        [style.--gl-stage]="group.color || null"
        [style.margin-left.px]="(group.depth ?? 0) * 16"
        [class.gl-group-shell--parent]="group.hasChildren"
      >
        @if (showGroupHeaders()) {
        <div class="gl-group-head flex items-center justify-between gap-3 min-h-9">
          <div class="flex items-center gap-2.5 min-w-0 flex-1">
            <button
              type="button"
              (click)="toggleGroupCollapsed(group.status)"
              class="gl-icon-btn h-8 w-8 flex items-center justify-center text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] shrink-0"
              [attr.aria-expanded]="!isGroupCollapsed(group.status)"
              [attr.aria-label]="group.label"
            >
              <lucide-icon
                [name]="isGroupCollapsed(group.status) ? 'chevron-right' : 'chevron-down'"
                class="h-4 w-4"
              ></lucide-icon>
            </button>
            @if (group.fieldLabel) {
              <span class="text-[11px] text-[var(--muted)] shrink-0">{{ group.fieldLabel }}:</span>
            }
            @if (group.color) {
              <span
                class="inline-flex items-center rounded-full py-1.5 px-3 text-[11px] font-semibold tracking-wide shrink-0 leading-none"
                [style.background-color]="stageBadgeBackground(group.color)"
                [style.color]="stageBadgeTextColor(group.color)"
                [style.border]="'1px solid ' + stageBadgeBorder(group.color)"
              >
                {{ group.label }}
              </span>
            } @else {
              <span class="text-sm font-semibold text-[var(--text)] truncate">{{ group.label }}</span>
            }
            @if (showGroupHeaderParenCount(group)) {
              <span class="tabular-nums text-[11px] font-medium shrink-0 text-[var(--muted)] opacity-70"
                >({{ group.items.length }})</span
              >
            }
          </div>
          <div class="flex items-center gap-1.5 shrink-0">
            @if (metricsRowFor(group); as sm) {
              <app-board-stage-metric-picker
                [sumFieldOptions]="stageMetricSumFieldOptions()"
                [config]="sm.storedConfig"
                [onTintedHeader]="false"
                [panelAria]="stageMetricPanelAria()"
                [triggerAriaLabel]="stageMetricTriggerAria()"
                (configChange)="
                  stageMetricSelectionChange.emit({ stageId: group.status, config: $event })
                "
              />
            }
            <button
              type="button"
              (click)="addItemInGroup.emit(group.status)"
              class="gl-icon-btn h-8 px-2.5 rounded-lg text-xs font-semibold text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] shrink-0 min-w-0"
            >
              <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
              {{ 'groupedList.actions.addItem' | t }}
            </button>
          </div>
        </div>

        @if (metricsRowFor(group); as smRow) {
          @if (smRow.chips.length) {
            <div class="flex flex-wrap items-center gap-2 mt-2.5 min-h-[22px]">
              <app-board-stage-metrics [onTintedHeader]="false" [chips]="smRow.chips" />
            </div>
          }
        }

        }
        @if (!group.hasChildren && !isGroupCollapsed(group.status)) {
          <div class="gl-group-divider" aria-hidden="true"></div>
          <div class="group-table-scroll">
            <div class="group-table-content">
              <div
                class="grouped-list-header gl-col-head grid gap-0 px-0"
                [style.grid-template-columns]="gridTemplateColumns()"
              >
                @if (selectionMode()) { <span aria-hidden="true"></span> }
                @for (col of columns(); track col.id) {
                  <div
                    class="min-w-0 flex items-center gl-cell-pad select-none"
                    [class.justify-center]="col.type === 'custom'"
                    [attr.data-column-id]="col.id"
                    [attr.title]="col.type === 'name' ? ('groupedList.columns.fixedTitle' | t) : null"
                  >
                    @if (col.type === 'name') {
                      <lucide-icon name="pin" class="h-3.5 w-3.5 mr-2 text-[var(--muted)] opacity-80 shrink-0"></lucide-icon>
                    }
                    <span
                      class="truncate min-w-0"
                      [class.w-full]="col.type === 'custom'"
                      [class.text-center]="col.type === 'custom'"
                      >{{ col.label }}</span
                    >
                  </div>
                  <div class="min-w-0 flex items-center justify-stretch">
                    <button
                      type="button"
                      (pointerdown)="startResize(col, $event)"
                      class="resize-handle w-full self-stretch cursor-col-resize border-l border-[color-mix(in_srgb,var(--border)_55%,transparent)] bg-transparent hover:bg-[var(--hover)] hover:border-[color-mix(in_srgb,var(--muted)_35%,var(--border))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]"
                      [attr.aria-label]="'groupedList.columns.resizeAria' | t"
                      [attr.title]="'groupedList.columns.resizeTitle' | t"
                    ></button>
                  </div>
                }
              </div>

              <ul class="gl-row-list list-none m-0 p-0">
                @for (item of group.items; track item.id) {
                  <li>
                    <div
                      class="gl-row-grid grid gap-0 px-0 items-stretch group min-w-0"
                      [class.gl-row-grid--colored]="!!item.rowColorHex"
                      [style.--gl-row-color]="item.rowColorHex || null"
                      [style.grid-template-columns]="gridTemplateColumns()"
                    >
                      @if (selectionMode()) {
                        <label class="flex items-center justify-center min-w-10" data-no-row-nav>
                          <input type="checkbox" class="h-4 w-4 accent-[var(--accent)] cursor-pointer"
                            [checked]="selectedItemIds().has(item.id)" [disabled]="selectionDisabled()"
                            [attr.aria-label]="'cardBulkDelete.selectCard' | t: { title: item.title }"
                            (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()"
                            (change)="itemSelectionToggle.emit(item.id)" />
                        </label>
                      }
                      @for (col of columns(); track col.id) {
                        <div
                          class="gl-cell-pad min-w-0 text-sm flex gap-2 min-h-0 py-0.5"
                          [class.truncate]="cellAllowsTruncate(col)"
                          [class.items-center]="!isCustomSelectColumn(col)"
                          [class.items-stretch]="isCustomSelectColumn(col)"
                        >
                          @switch (col.type) {
                            @case ('name') {
                              <div class="inline-flex items-center gap-2.5 min-w-0 w-full">
                                <div class="inline-flex items-center gap-0.5 shrink-0">
                                  @if (canMoveUp(item)) {
                                    <button
                                      type="button"
                                      class="gl-icon-btn h-8 w-8 border border-[color-mix(in_srgb,var(--border)_70%,transparent)] bg-[var(--bg-surface)] text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] inline-flex items-center justify-center"
                                      [attr.title]="'groupedList.actions.movePrevious' | t"
                                      (click)="onMoveUp(item, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                    >
                                      <lucide-icon name="chevron-up" class="h-3.5 w-3.5"></lucide-icon>
                                    </button>
                                  }
                                  @if (canMoveDown(item)) {
                                    <button
                                      type="button"
                                      class="gl-icon-btn h-8 w-8 border border-[color-mix(in_srgb,var(--border)_70%,transparent)] bg-[var(--bg-surface)] text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] inline-flex items-center justify-center"
                                      [attr.title]="'groupedList.actions.moveNext' | t"
                                      (click)="onMoveDown(item, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                    >
                                      <lucide-icon name="chevron-down" class="h-3.5 w-3.5"></lucide-icon>
                                    </button>
                                  }
                                </div>
                                @if (group.color) {
                                  <span
                                    class="w-1.5 h-1.5 rounded-full shrink-0 opacity-90"
                                    [style.background-color]="group.color"
                                  ></span>
                                }
                                @if (isEditingCell(item.id, col.id)) {
                                  <input
                                    type="text"
                                    class="min-w-0 flex-1 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] px-1 py-0.5 text-sm font-semibold text-[var(--text)] shadow-none focus:border-[var(--focus)] focus:outline-none focus:ring-1 focus:ring-[var(--focus)]"
                                    [value]="item.title"
                                    (mousedown)="$event.stopPropagation()"
                                    (click)="$event.stopPropagation()"
                                    (keydown.enter)="$event.preventDefault(); $any($event.target).blur()"
                                    (blur)="onTitleBlur(item, $event)"
                                    [attr.aria-label]="'groupedList.inline.titleAria' | t"
                                  />
                                } @else {
                                  <div class="min-w-0 flex-1 inline-flex items-center gap-2">
                                    <button
                                      type="button"
                                      class="gl-icon-btn h-8 w-8 shrink-0 border border-[color-mix(in_srgb,var(--border)_65%,transparent)] bg-[color-mix(in_srgb,var(--bg-surface)_92%,var(--hover))] text-[var(--muted)] opacity-85 hover:opacity-100 hover:bg-[var(--hover)] hover:text-[var(--text)] inline-flex items-center justify-center"
                                      (click)="openItemDetails(item, $event)"
                                      [attr.aria-label]="'groupedList.actions.open' | t"
                                      [attr.title]="'groupedList.actions.open' | t"
                                    >
                                      <lucide-icon name="external-link" class="h-3.5 w-3.5"></lucide-icon>
                                    </button>
                                    <button
                                      type="button"
                                      class="min-w-0 flex-1 text-left text-[13px] font-semibold leading-snug text-[var(--text)] line-clamp-2 whitespace-normal [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                                      (click)="onNameClick(item.id, col.id, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                      (dblclick)="onNameDoubleClick(item, $event)"
                                    >
                                      {{ item.title }}
                                    </button>
                                    @if (pendingReplyCardIds().has(item.id)) {
                                      <span class="shrink-0 text-[var(--accent)]" role="img" [attr.aria-label]="'externalReplies.pending' | t" [attr.title]="'externalReplies.pending' | t">
                                        <lucide-icon name="message-circle" [size]="16" />
                                      </span>
                                    }
                                  </div>
                                }
                              </div>
                            }
                            @case ('code') {
                              <span class="text-xs font-semibold text-[var(--muted)] tabular-nums truncate flex-1 min-w-0">{{
                                item.code || '—'
                              }}</span>
                              @if (item.code?.trim()) {
                                <app-cf-inline-copy-button [textToCopy]="item.code!" [hoverOnly]="true" />
                              }
                            }
                            @case ('latestComment') {
                              <span
                                class="latest-comment-preview block w-full min-w-0 self-center truncate text-[12px] leading-snug text-[var(--muted)]"
                                [attr.title]="item.latestComment || null"
                              >{{ item.latestComment?.trim() || '—' }}</span>
                            }
                            @case ('description') {
                              <span
                                class="block w-full min-w-0 self-center truncate text-[12px] leading-snug text-[color-mix(in_srgb,var(--muted)_82%,var(--text))]"
                                [attr.title]="descriptionListTooltip(item.description)"
                                >{{ item.description?.trim() || '—' }}</span
                              >
                            }
                            @case ('status') {
                              <div
                                class="flex items-center gap-2 min-w-0 w-full"
                                data-no-row-nav
                                (click)="stopRowNavigation($event)"
                                (mousedown)="stopRowNavigation($event)"
                              >
                                @if (group.color) {
                                  <span
                                    class="w-1.5 h-1.5 rounded-full shrink-0"
                                    [style.background-color]="group.color"
                                  ></span>
                                }
                                @if (isEditingCell(item.id, col.id)) {
                                  <div
                                    class="w-full min-w-0"
                                    (click)="stopRowNavigation($event)"
                                    (mousedown)="stopRowNavigation($event)"
                                    (pointerdown)="stopRowNavigation($event)"
                                  >
                                    <app-combobox
                                      [options]="statusSelectOptions()"
                                      [value]="item.status"
                                      [variant]="'select'"
                                      [searchPlaceholder]="'Pesquisar etapa...'"
                                      [emptyLabel]="'Nenhuma etapa'"
                                      [closedPlaceholder]="'Selecionar'"
                                      [fixedPanel]="true"
                                      (valueChange)="onStatusComboboxChange(item, $event)"
                                    />
                                  </div>
                                } @else {
                                  <button
                                    type="button"
                                    class="h-7 w-full min-w-0 rounded-md border border-transparent bg-transparent px-1 text-left text-xs text-[var(--text)]"
                                    (click)="startEditingCell(item.id, col.id, $event)"
                                    (mousedown)="preventRowNavigation($event)"
                                  >
                                    {{ item.status }}
                                  </button>
                                }
                              </div>
                            }
                            @case ('assignee') {
                                  <div
                                    class="flex items-center gap-2 min-w-0 w-full min-h-0 py-0"
                                    (click)="stopRowNavigation($event)"
                                    (mousedown)="stopRowNavigation($event)"
                                  >
                                @if (assignees().length > 0) {
                                  @if (isEditingCell(item.id, col.id)) {
                                    <div
                                      class="w-full min-w-0"
                                      (click)="stopRowNavigation($event)"
                                      (mousedown)="stopRowNavigation($event)"
                                      (pointerdown)="stopRowNavigation($event)"
                                    >
                                      <app-combobox
                                        [personLayout]="assigneeOptionGroups().length > 0"
                                        [optionGroups]="assigneeOptionGroups().length ? assigneeOptionGroups() : null"
                                        [options]="assigneeComboboxFlatOptions()"
                                        [value]="item.assigneeName"
                                        [variant]="'select'"
                                        [appearance]="'table-cell'"
                                        [avatarSizePx]="26"
                                        [searchPlaceholder]="'Buscar nome ou e-mail…'"
                                        [emptyLabel]="'Nenhum resultado'"
                                        [closedPlaceholder]="'Selecionar'"
                                        [fixedPanel]="true"
                                        (valueChange)="onAssigneeComboboxChange(item, $event)"
                                      />
                                    </div>
                                  } @else {
                                    <button
                                      type="button"
                                      class="gl-icon-btn h-8 w-full min-w-0 rounded-lg border border-transparent bg-transparent px-1 flex items-center justify-start gap-2 text-left text-xs text-[var(--text)]"
                                      (click)="startEditingCell(item.id, col.id, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                      [attr.title]="hasAssigneeDisplay(item) ? item.assigneeName : null"
                                    >
                                      @if (hasAssigneeDisplay(item)) {
                                        <span
                                          class="inline-flex rounded-full p-[1.5px] shrink-0"
                                          style="background: color-mix(in srgb, var(--border) 55%, transparent)"
                                        >
                                          <app-user-avatar class="shrink-0"
                                            [userId]="assigneeResolvedUserId(item)"
                                            [label]="item.assigneeName"
                                            [size]="26"
                                          />
                                        </span>
                                      } @else {
                                        <span class="text-[var(--muted)]">—</span>
                                      }
                                    </button>
                                  }
                                } @else {
                                  @if (hasAssigneeDisplay(item)) {
                                    <span
                                      class="inline-flex rounded-full p-[1.5px] shrink-0"
                                      style="background: color-mix(in srgb, var(--border) 55%, transparent)"
                                      [attr.title]="item.assigneeName"
                                    >
                                      <app-user-avatar class="shrink-0"
                                        [userId]="assigneeResolvedUserId(item)"
                                        [label]="item.assigneeName"
                                        [size]="30"
                                      />
                                    </span>
                                  } @else {
                                    <span class="font-normal text-[var(--muted)]">—</span>
                                  }
                                }
                              </div>
                            }
                            @case ('priority') {
                              <div
                                class="flex items-center gap-2 min-w-0 w-full"
                                (click)="stopRowNavigation($event)"
                                (mousedown)="stopRowNavigation($event)"
                              >
                                @if (priorities().length > 0) {
                                  @if (isEditingCell(item.id, col.id)) {
                                    <div
                                      class="w-full min-w-0"
                                      (click)="stopRowNavigation($event)"
                                      (mousedown)="stopRowNavigation($event)"
                                      (pointerdown)="stopRowNavigation($event)"
                                    >
                                      <app-combobox
                                        [options]="prioritySelectOptionsFor(item)"
                                        [value]="prioritySelectModel(item)"
                                        [variant]="'select'"
                                        [searchPlaceholder]="'Pesquisar prioridade...'"
                                        [emptyLabel]="'Nenhuma opção'"
                                        [closedPlaceholder]="'Selecionar'"
                                        [fixedPanel]="true"
                                        (valueChange)="onPriorityComboboxChange(item, $event)"
                                      />
                                    </div>
                                  } @else {
                                    <button
                                      type="button"
                                      class="gl-icon-btn min-h-[26px] h-full w-full min-w-0 shrink-0 inline-flex items-center justify-center rounded-lg border border-transparent bg-transparent px-1"
                                      (click)="startEditingCell(item.id, col.id, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                    >
                                      <span
                                        class="gl-priority-pill truncate max-w-full"
                                        [class.gl-priority-pill--low]="priorityVisualKind(item) === 'low'"
                                        [class.gl-priority-pill--med]="priorityVisualKind(item) === 'medium'"
                                        [class.gl-priority-pill--high]="priorityVisualKind(item) === 'high'"
                                        [class.gl-priority-pill--default]="priorityVisualKind(item) === 'default'"
                                        >{{ item.priority }}</span
                                      >
                                    </button>
                                  }
                                } @else {
                                  <span
                                    class="gl-priority-pill truncate max-w-full"
                                    [class.gl-priority-pill--low]="priorityVisualKind(item) === 'low'"
                                    [class.gl-priority-pill--med]="priorityVisualKind(item) === 'medium'"
                                    [class.gl-priority-pill--high]="priorityVisualKind(item) === 'high'"
                                    [class.gl-priority-pill--default]="priorityVisualKind(item) === 'default'"
                                    >{{ item.priority }}</span
                                  >
                                }
                              </div>
                            }
                            @case ('dueDate') {
                              <div
                                class="flex items-center gap-2 min-w-0 w-full"
                                (click)="stopRowNavigation($event)"
                                (mousedown)="stopRowNavigation($event)"
                              >
                                @if (isEditingCell(item.id, col.id)) {
                                  <div
                                    class="w-full min-w-0 flex flex-col gap-1.5"
                                    (click)="stopRowNavigation($event)"
                                    (mousedown)="stopRowNavigation($event)"
                                    (pointerdown)="stopRowNavigation($event)"
                                  >
                                    <app-date-picker
                                      [value]="dueDatePickerValue(item)"
                                      (valueChange)="onDueDatePickerChange(item, $event)"
                                    />
                                    <button
                                      type="button"
                                      class="text-left text-xs font-medium text-[var(--muted)] hover:text-[var(--text)] transition-colors"
                                      (click)="onDueDateClear(item, $event)"
                                    >
                                      {{ 'groupedList.inline.clearDueDate' | t }}
                                    </button>
                                  </div>
                                } @else {
                                  <button
                                    type="button"
                                    class="gl-due-btn gl-icon-btn h-8 w-full min-w-0 border border-transparent bg-transparent px-1.5 text-left text-[11px] text-[color-mix(in_srgb,var(--muted)_88%,var(--text))]"
                                    [class.gl-due--soon]="dueDateVisualKind(item) === 'soon'"
                                    [class.gl-due--overdue]="dueDateVisualKind(item) === 'overdue'"
                                    (click)="startEditingCell(item.id, col.id, $event)"
                                    (mousedown)="preventRowNavigation($event)"
                                  >
                                    {{ item.dueDate ? formatDateFn()(item.dueDate) : '—' }}
                                  </button>
                                }
                              </div>
                            }
                            @case ('custom') {
                              @if (customEditorFor(col); as editor) {
                                @if (editor.kind === 'rating') {
                                  <div
                                    class="w-full min-w-0"
                                    (click)="stopRowNavigation($event)"
                                    (mousedown)="stopRowNavigation($event)"
                                    (pointerdown)="stopRowNavigation($event)"
                                  >
                                    <app-cf-rating-field
                                      [field]="ratingFieldViewModel(col, editor)"
                                      [value]="ratingRawValue(item, col)"
                                      [showLabel]="false"
                                      [compact]="true"
                                      [clearPresentation]="'icon'"
                                      (valueChanged)="onCfRatingChanged(item, col, $event)"
                                    />
                                  </div>
                                } @else if (editor.kind === 'file') {
                                  <div
                                    class="w-full min-w-0 flex items-center overflow-visible"
                                    (click)="stopRowNavigation($event)"
                                    (mousedown)="stopRowNavigation($event)"
                                    (pointerdown)="stopRowNavigation($event)"
                                  >
                                    <app-list-file-field-cell
                                      [cardId]="item.id"
                                      [rawValue]="fileFieldRawValue(item, col)"
                                      (fileSelected)="onListFileFieldPicked(item, col, $event)"
                                    />
                                  </div>
                                } @else if (editor.kind === 'item-list') {
                                  <div class="w-full min-w-0" (click)="stopRowNavigation($event)" (mousedown)="stopRowNavigation($event)" (pointerdown)="stopRowNavigation($event)">
                                    <app-cf-item-list-field [field]="itemListFieldViewModel(col, editor)"
                                      [value]="ratingRawValue(item, col)" [compact]="true" [showLabel]="false"
                                      (valueChanged)="onItemListChanged(item, col, $event)" />
                                  </div>
                                } @else if (editor.kind === 'confirmation-date') {
                                  @let confirmation = cellValue(item, col);
                                  <span class="inline-flex items-center justify-center gap-2 w-full min-w-0 whitespace-nowrap text-xs font-medium text-[var(--muted)]">
                                    @if (confirmation !== '—') {
                                      <span class="shrink-0 font-bold text-[var(--brand-text)]">✓</span>
                                      <span class="shrink-0" aria-hidden="true">-</span>
                                      <span class="tabular-nums">{{ confirmation.slice(2) }}</span>
                                    } @else {
                                      —
                                    }
                                  </span>
                                } @else if (editor.kind === 'boolean') {
                                  <button
                                    type="button"
                                    class="gl-boolean-chip"
                                    (click)="onCustomFieldBooleanToggle(item, col, $event)"
                                    (mousedown)="preventRowNavigation($event)"
                                    (pointerdown)="preventRowNavigation($event)"
                                  >
                                    <span
                                      class="gl-boolean-switch"
                                      [class.gl-boolean-switch--checked]="isCustomBooleanChecked(item, col)"
                                    ></span>
                                    <span class="gl-boolean-chip__text">{{ booleanCellText(item, col) }}</span>
                                  </button>
                                } @else if (isEditingCell(item.id, col.id)) {
                                  @if (editor.kind === 'select') {
                                    <div
                                      class="w-full min-w-0 h-full min-h-[24px] flex flex-col"
                                      (click)="stopRowNavigation($event)"
                                      (mousedown)="stopRowNavigation($event)"
                                      (pointerdown)="stopRowNavigation($event)"
                                    >
                                      <app-combobox
                                        [personLayout]="editor.displayAsPerson ?? false"
                                        [options]="customSelectOptions(item, col, editor)"
                                        [value]="(col.customFieldKey && item.customFields ? item.customFields[col.customFieldKey] : '') || ''"
                                        [variant]="'select'"
                                        [appearance]="'table-cell'"
                                        [avatarSizePx]="28"
                                        [searchPlaceholder]="editor.displayAsPerson ? 'Buscar nome ou e-mail…' : 'Pesquise ou adicione opções...'"
                                        [emptyLabel]="'Sem opções'"
                                        [closedPlaceholder]="editor.emptyLabel || 'Selecionar'"
                                        [fixedPanel]="true"
                                        (valueChange)="onCustomFieldValueCommit(item, col, $event)"
                                      />
                                    </div>
                                  } @else if (editor.kind === 'date') {
                                    <div
                                      class="w-full min-w-0 flex flex-col gap-1.5"
                                      (click)="stopRowNavigation($event)"
                                      (mousedown)="stopRowNavigation($event)"
                                      (pointerdown)="stopRowNavigation($event)"
                                    >
                                      <app-date-picker
                                        [value]="customFieldDatePickerValue(item, col)"
                                        (valueChange)="onCustomFieldDatePickerChange(item, col, $event)"
                                      />
                                      <button
                                        type="button"
                                        class="text-left text-xs font-medium text-[var(--muted)] hover:text-[var(--text)] transition-colors"
                                        (click)="onCustomFieldDateClear(item, col, $event)"
                                      >
                                        {{ 'groupedList.inline.clearDueDate' | t }}
                                      </button>
                                    </div>
                                  } @else if (editor.kind === 'number') {
                                    <div
                                      class="w-full min-w-0 flex items-center gap-1"
                                      (click)="stopRowNavigation($event)"
                                      (mousedown)="stopRowNavigation($event)"
                                      (pointerdown)="stopRowNavigation($event)"
                                    >
                                      <input
                                        type="text"
                                        inputmode="decimal"
                                        class="h-7 min-w-0 flex-1 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] px-1 text-xs text-[var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]"
                                        [value]="customFieldPlainInputDisplay(item, col)"
                                        (blur)="onCustomFieldCommit(item, col, $event)"
                                      />
                                      @if (editor.numberShowPercentSuffix) {
                                        <span class="shrink-0 text-[11px] font-semibold text-[var(--muted)]"
                                          >%</span
                                        >
                                      }
                                    </div>
                                  } @else {
                                    <input
                                      type="text"
                                      class="h-7 w-full min-w-0 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] px-1 text-xs text-[var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]"
                                      [attr.inputmode]="editor.kind === 'phone' ? 'tel' : null"
                                      [attr.maxlength]="editor.kind === 'phone' ? 15 : null"
                                      [value]="customFieldPlainInputDisplay(item, col)"
                                      (blur)="onCustomFieldCommit(item, col, $event)"
                                      (click)="stopRowNavigation($event)"
                                      (mousedown)="stopRowNavigation($event)"
                                      (pointerdown)="stopRowNavigation($event)"
                                    />
                                  }
                                } @else {
                                  @if (editor.displayAsPerson) {
                                    @if (personFieldLabel(item, col).trim().length > 0) {
                                      <button
                                        type="button"
                                        class="min-h-[24px] h-full w-full min-w-0 shrink-0 rounded-[3px] border border-transparent bg-transparent px-1 inline-flex items-center justify-center"
                                        (click)="startEditingCell(item.id, col.id, $event)"
                                        (mousedown)="preventRowNavigation($event)"
                                      >
                                        <app-user-avatar class="shrink-0"
                                          [userId]="personFieldUserId(item, col)"
                                          [label]="personFieldLabel(item, col)"
                                          [size]="28"
                                        />
                                      </button>
                                    } @else {
                                      <button
                                        type="button"
                                        class="min-h-[24px] h-full w-full min-w-0 shrink-0 rounded-[3px] border border-transparent bg-transparent px-1 text-center text-xs font-medium text-[var(--muted)]"
                                        (click)="startEditingCell(item.id, col.id, $event)"
                                        (mousedown)="preventRowNavigation($event)"
                                      >
                                        —
                                      </button>
                                    }
                                  } @else if (customCellColor(item, col); as cellColor) {
                                    <button
                                      type="button"
                                      class="w-full min-h-[26px] h-full shrink-0 inline-flex items-center justify-center rounded-lg px-2 py-1 text-[11px] font-bold truncate text-center"
                                      [style.background-color]="cellColor"
                                      [style.color]="customCellTextColor(cellColor)"
                                      (click)="startEditingCell(item.id, col.id, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                    >
                                      {{ cellValue(item, col) }}
                                    </button>
                                  } @else {
                                    <button
                                      type="button"
                                      class="min-h-[24px] h-full min-w-0 shrink-0 rounded-[3px] border border-transparent bg-transparent px-1 text-center text-xs font-bold text-[var(--muted)] truncate flex-1"
                                      (click)="startEditingCell(item.id, col.id, $event)"
                                      (mousedown)="preventRowNavigation($event)"
                                    >
                                      {{ cellValue(item, col) }}
                                    </button>
                                    @if (isCopyableCell(col) && cellCopyText(item, col); as copyText) {
                                      <app-cf-inline-copy-button [textToCopy]="copyText" [hoverOnly]="true" />
                                    }
                                  }
                                }
                              } @else {
                                @if (customCellColor(item, col); as cellColor) {
                                  <span
                                    class="w-full min-h-[26px] inline-flex items-center justify-center rounded-lg px-2 py-1 text-[11px] font-bold truncate text-center"
                                    [style.background-color]="cellColor"
                                    [style.color]="customCellTextColor(cellColor)"
                                  >
                                    {{ cellValue(item, col) }}
                                  </span>
                                } @else {
                                  <span class="font-normal text-[var(--muted)] truncate flex-1 min-w-0">{{ cellValue(item, col) }}</span>
                                  @if (isCopyableCell(col) && cellCopyText(item, col); as copyText) {
                                    <app-cf-inline-copy-button [textToCopy]="copyText" [hoverOnly]="true" />
                                  }
                                }
                              }
                            }
                            @default {
                              <span class="font-normal text-[var(--muted)] truncate">{{ cellValue(item, col) }}</span>
                            }
                          }
                        </div>
                        <div
                          class="min-w-0 border-l border-[color-mix(in_srgb,var(--border)_48%,transparent)]"
                          aria-hidden="true"
                        ></div>
                      }
                    </div>
                  </li>
                }
              </ul>
              @if (group.items.length === 0) {
                <div class="py-4 text-center text-sm text-[var(--muted)]">{{ 'groupedList.empty' | t }}</div>
              }
            </div>
          </div>
        }
      </section>
    }
  </div>
} @else {
  <div class="space-y-0">
    @for (group of visibleGroups(); track group.status; let first = $first) {
      <section
        class="gl-group-shell pt-6 mt-6 px-4 py-4"
        [class.mt-0]="first"
        [class.pt-0]="first"
        [class.gl-group-shell--stage]="!!group.color"
        [style.--gl-stage]="group.color || null"
        [style.margin-left.px]="(group.depth ?? 0) * 16"
        [class.gl-group-shell--parent]="group.hasChildren"
      >
        @if (showGroupHeaders()) {
        <div class="gl-group-head flex items-center justify-between gap-3 min-h-9">
          <div class="flex items-center gap-2.5 min-w-0 flex-1">
            <button
              type="button"
              (click)="toggleGroupCollapsed(group.status)"
              class="gl-icon-btn h-8 w-8 flex items-center justify-center text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] shrink-0"
              [attr.aria-expanded]="!isGroupCollapsed(group.status)"
              [attr.aria-label]="group.label"
            >
              <lucide-icon
                [name]="isGroupCollapsed(group.status) ? 'chevron-right' : 'chevron-down'"
                class="h-4 w-4"
              ></lucide-icon>
            </button>
            @if (group.fieldLabel) {
              <span class="text-[11px] text-[var(--muted)] shrink-0">{{ group.fieldLabel }}:</span>
            }
            @if (group.color) {
              <span
                class="inline-flex items-center rounded-full py-1.5 px-3 text-[11px] font-semibold tracking-wide shrink-0 leading-none"
                [style.background-color]="stageBadgeBackground(group.color)"
                [style.color]="stageBadgeTextColor(group.color)"
                [style.border]="'1px solid ' + stageBadgeBorder(group.color)"
              >
                {{ group.label }}
              </span>
            } @else {
              <span class="text-sm font-semibold text-[var(--text)] truncate">{{ group.label }}</span>
            }
            @if (showGroupHeaderParenCount(group)) {
              <span class="tabular-nums text-[11px] font-medium shrink-0 text-[var(--muted)] opacity-70"
                >({{ group.items.length }})</span
              >
            }
          </div>
          <div class="flex items-center gap-1.5 shrink-0">
            @if (metricsRowFor(group); as sm) {
              <app-board-stage-metric-picker
                [sumFieldOptions]="stageMetricSumFieldOptions()"
                [config]="sm.storedConfig"
                [onTintedHeader]="false"
                [panelAria]="stageMetricPanelAria()"
                [triggerAriaLabel]="stageMetricTriggerAria()"
                (configChange)="
                  stageMetricSelectionChange.emit({ stageId: group.status, config: $event })
                "
              />
            }
            <button
              type="button"
              (click)="addItemInGroup.emit(group.status)"
              class="gl-icon-btn h-8 px-2.5 rounded-lg text-xs font-semibold text-[var(--muted)] hover:bg-[var(--hover)] hover:text-[var(--text)] flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] shrink-0 min-w-0"
            >
              <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
              {{ 'groupedList.actions.addItem' | t }}
            </button>
          </div>
        </div>

        @if (metricsRowFor(group); as smRow) {
          @if (smRow.chips.length) {
            <div class="flex flex-wrap items-center gap-2 mt-2.5 min-h-[22px]">
              <app-board-stage-metrics [onTintedHeader]="false" [chips]="smRow.chips" />
            </div>
          }
        }

        }
        @if (!group.hasChildren && !isGroupCollapsed(group.status)) {
          <div class="gl-group-divider" aria-hidden="true"></div>
          <ul class="gl-row-list list-none m-0 p-0">
            @for (item of group.items; track item.id) {
              <li>
                <div
                  class="gl-row-grid flex items-center gap-3 px-1 min-h-[34px] transition-[background-color,box-shadow] duration-150 ease-out group min-w-0"
                >
                  @if (selectionMode()) {
                        <label class="flex items-center justify-center min-w-10" data-no-row-nav>
                          <input type="checkbox" class="h-4 w-4 accent-[var(--accent)] cursor-pointer"
                            [checked]="selectedItemIds().has(item.id)" [disabled]="selectionDisabled()"
                            [attr.aria-label]="'cardBulkDelete.selectCard' | t: { title: item.title }"
                            (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()"
                            (change)="itemSelectionToggle.emit(item.id)" />
                        </label>
                      }
                  @if (group.color) {
                    <span class="w-1.5 h-1.5 rounded-full shrink-0" [style.background-color]="group.color"></span>
                  }
                  <button
                    type="button"
                    class="gl-icon-btn h-8 w-8 shrink-0 border border-[color-mix(in_srgb,var(--border)_65%,transparent)] bg-[color-mix(in_srgb,var(--bg-surface)_92%,var(--hover))] text-[var(--muted)] opacity-85 hover:opacity-100 hover:bg-[var(--hover)] hover:text-[var(--text)] inline-flex items-center justify-center"
                    (click)="openItemDetails(item, $event)"
                    [attr.aria-label]="'groupedList.actions.open' | t"
                    [attr.title]="'groupedList.actions.open' | t"
                  >
                    <lucide-icon name="external-link" class="h-3.5 w-3.5"></lucide-icon>
                  </button>
                  <button
                    type="button"
                    class="flex-1 min-w-0 text-left text-[13px] font-semibold leading-snug text-[var(--text)] line-clamp-2 whitespace-normal [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                    (click)="onNameClick(item.id, 'name', $event)"
                    (mousedown)="preventRowNavigation($event)"
                    (dblclick)="onNameDoubleClick(item, $event)"
                  >
                    {{ item.title }}
                  </button>
                  <div class="flex items-center gap-2 shrink-0 text-xs font-normal text-[var(--muted)]">
                    @if (hasAssigneeDisplay(item)) {
                      <span
                        class="inline-flex rounded-full p-[1.5px] shrink-0"
                        style="background: color-mix(in srgb, var(--border) 55%, transparent)"
                        [attr.title]="item.assigneeName"
                      >
                        <app-user-avatar class="shrink-0"
                          [userId]="assigneeResolvedUserId(item)"
                          [label]="item.assigneeName"
                          [size]="30"
                        />
                      </span>
                    }
                    <span
                      class="gl-priority-pill"
                      [class.gl-priority-pill--low]="priorityVisualKind(item) === 'low'"
                      [class.gl-priority-pill--med]="priorityVisualKind(item) === 'medium'"
                      [class.gl-priority-pill--high]="priorityVisualKind(item) === 'high'"
                      [class.gl-priority-pill--default]="priorityVisualKind(item) === 'default'"
                      >{{ item.priority }}</span
                    >
                    @if (item.dueDate) {
                      <span
                        class="gl-due-btn rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-[color-mix(in_srgb,var(--muted)_88%,var(--text))]"
                        [class.gl-due--soon]="dueDateVisualKind(item) === 'soon'"
                        [class.gl-due--overdue]="dueDateVisualKind(item) === 'overdue'"
                        >{{ formatDateFn()(item.dueDate) }}</span
                      >
                    }
                    <span class="font-medium text-[var(--active-text)] opacity-0 group-hover:opacity-100 transition-opacity">{{
                      'groupedList.actions.open' | t
                    }}</span>
                  </div>
                </div>
              </li>
            }
          </ul>
          @if (group.items.length === 0) {
            <div class="py-4 text-center text-sm text-[var(--muted)]">{{ 'groupedList.empty' | t }}</div>
          }
        }
      </section>
    }
  </div>
}
